import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle, 
  Globe2, 
  BookOpen, 
  Sparkles 
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const academicLinks = [
  {
    name: 'Google Scholar',
    url: 'https://scholar.google.co.in/citations?user=1ntflp8AAAAJ&hl=en&authuser=5',
    color: '#4285F4',
    desc: 'Citations, h-index & research metrics'
  },
  {
    name: 'Scopus Profile',
    url: 'https://www.scopus.com/authid/detail.uri?authorId=56966376600',
    color: '#FF6D00',
    desc: 'Elsevier author identifier & indexed outputs'
  },
  {
    name: 'ORCID Record',
    url: 'https://orcid.org/0000-0001-6754-9602',
    color: '#A6CE39',
    desc: '0000-0001-6754-9602 (Persistent digital ID)'
  },
  {
    name: 'ResearchGate',
    url: 'https://www.researchgate.net/profile/Sanjay-Dhir-3',
    color: '#00CCBB',
    desc: 'Pre-prints, collaborations & impact reads'
  },
  {
    name: 'Publons / Web of Science',
    url: 'https://publons.com/researcher/4818681/sanjay-dhir/',
    color: '#8A2BE2',
    desc: 'Peer review and editorial tracking'
  }
];

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', comment: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: '' });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: '' });

    try {
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus({ loading: false, success: true, error: '' });
        setFormData({ name: '', email: '', comment: '' });
      } else {
        setStatus({ loading: false, success: false, error: data.message || 'Failed to submit form.' });
      }
    } catch (err) {
      setStatus({ loading: false, success: false, error: 'Cannot connect to server. Please try again later.' });
    }
  };

  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-4 mb-5">
        <div className="col-12 col-lg-8" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <Mail size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Get in Touch</span>
          </div>

          <h1 className="display-5 fw-bold text-white mb-3">
            Contact & <span className="text-gradient">Academic Office</span>
          </h1>

          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Reach out regarding doctoral guidance, executive consulting, research collaborations, 
            or institutional advisory at the Department of Management Studies, IIT Delhi.
          </p>

          <div className="d-flex flex-wrap gap-3">
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '120px' }}>
              <span className="fs-5 fw-bold text-cyan">DMS, IITD</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Strategic Area Chair</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '120px' }}>
              <span className="fs-5 fw-bold text-warning">New Delhi</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Hauz Khas Campus</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '120px' }}>
              <span className="fs-5 fw-bold text-info">FPM (Ph.D.)</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>IIM Lucknow</span>
            </div>
          </div>
        </div>

        {/* Office Snapshot Card */}
        <div className="col-12 col-lg-4" data-aos="fade-left">
          <div className="p-4 rounded-4 glass-panel border-glow text-center d-flex flex-column align-items-center justify-content-center h-100">
            <MapPin size={40} className="text-cyan mb-3" />
            <h5 className="text-white fw-bold mb-2">Faculty Chambers</h5>
            <p className="text-secondary small mb-0 lh-base">
              Department of Management Studies (DMS), IV Floor, Vishwakarma Bhavan, IIT Delhi, Hauz Khas, New Delhi – 110016, India.
            </p>
          </div>
        </div>
      </div>

      {/* 2. Main Interactive Row: Info Left, Form Right */}
      <div className="row g-4 mb-5">
        
        {/* Left Column: Direct Info & Academic Indices */}
        <div className="col-12 col-lg-5" data-aos="fade-right">
          <div className="p-4 p-md-5 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between">
            <div>
              <h4 className="text-white fw-bold mb-3">Dr. Sanjay Dhir</h4>
              <p className="text-info small mb-4">
                Associate Professor and Area Chair, Strategic Management Area<br />
                Department of Management Studies, IIT Delhi
              </p>

              {/* Contact Specifics */}
              <div className="d-flex flex-column gap-3 mb-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 rounded-circle bg-dark border border-secondary border-opacity-50">
                    <Mail size={18} className="text-cyan" />
                  </div>
                  <div>
                    <span className="small text-secondary d-block">Official Email</span>
                    <a href="mailto:sanjaydhir@dms.iitd.ac.in" className="text-light fw-medium text-decoration-none hover-cyan">
                      sanjaydhir@dms.iitd.ac.in
                    </a>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 rounded-circle bg-dark border border-secondary border-opacity-50">
                    <Phone size={18} className="text-warning" />
                  </div>
                  <div>
                    <span className="small text-secondary d-block">Phone (Office)</span>
                    <a href="tel:+911126596386" className="text-light fw-medium text-decoration-none hover-cyan">
                      +91-11-26596386
                    </a>
                  </div>
                </div>
              </div>

              {/* Verified Scholar Identifiers */}
              <h6 className="text-white fw-bold mb-3 d-flex align-items-center gap-2">
                <Globe2 size={16} className="text-cyan" /> Academic & Citation Profiles
              </h6>
              <div className="d-flex flex-column gap-2">
                {academicLinks.map((item, idx) => (
                  <a
                    key={idx}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25 d-flex align-items-center justify-content-between text-decoration-none transition-all hover-border-glow"
                  >
                    <div>
                      <div className="fw-semibold text-light small d-flex align-items-center gap-2">
                        <span style={{ color: item.color }}>●</span> {item.name}
                      </div>
                      <span className="text-secondary" style={{ fontSize: '0.72rem' }}>
                        {item.desc}
                      </span>
                    </div>
                    <ExternalLink size={14} className="text-secondary" />
                  </a>
                ))}
              </div>
            </div>

            <div className="pt-4 mt-4 border-top border-secondary border-opacity-25 text-secondary small">
              Visiting Scholars & Meeting Hours: By prior email appointment only.
            </div>
          </div>
        </div>

        {/* Right Column: Interactive Email Dispatch Form */}
        <div className="col-12 col-lg-7" data-aos="fade-left">
          <div className="p-4 p-md-5 rounded-4 glass-panel h-100">
            <h4 className="text-white fw-bold mb-2">Send a Direct Message</h4>
            <p className="text-secondary small mb-4">
              Submissions are delivered directly to Dr. Sanjay Dhir's administrative inbox.
            </p>

            {status.success && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="alert alert-success d-flex align-items-center gap-2 py-3 rounded-3 mb-4"
                style={{ background: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.4)', color: '#34d399' }}
              >
                <CheckCircle2 size={18} />
                <span>Thank you. Your message has been sent successfully!</span>
              </motion.div>
            )}

            {status.error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="alert alert-danger d-flex align-items-center gap-2 py-3 rounded-3 mb-4"
                style={{ background: 'rgba(239, 68, 68, 0.15)', borderColor: 'rgba(239, 68, 68, 0.4)', color: '#f87171' }}
              >
                <AlertCircle size={18} />
                <span>{status.error}</span>
              </motion.div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Dr. Jane Doe"
                    className="form-control rounded-3 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary">Your Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. jane@institution.edu"
                    className="form-control rounded-3 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label small text-secondary">Message / Inquiry *</label>
                  <textarea
                    name="comment"
                    required
                    rows="8"
                    value={formData.comment}
                    onChange={handleChange}
                    placeholder="Please mention the purpose of your communication (e.g., Doctoral Inquiry, Conference Keynote, MDP Consultation)..."
                    className="form-control rounded-3 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
                  />
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  disabled={status.loading}
                  className="btn btn-info text-dark fw-bold px-4 py-2 rounded-pill d-inline-flex align-items-center gap-2 transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #00f2fe, #4facfe)',
                    boxShadow: '0 0 16px rgba(0, 242, 254, 0.35)',
                    border: 'none'
                  }}
                >
                  {status.loading ? (
                    <span>Sending Transmission...</span>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Transmit Message</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

      </div>

    </div>
  );
}